import {
  AutofaxMessage,
  CaseIntegrationMessage,
  ECWOrderMessage,
  ECWResultMessage,
  EZDermResultMessage,
  ModMedOrderMessage,
  ModMedResultMessage,
  NextechResultMessage,
  SonicOrderMessage,
  SonicResultMessage,
  WPLOrderMessage,
  WPLResultMessage,
} from "pwapi";

export type StatusVariant =
  | "success"
  | "error"
  | "pending"
  | "hold"
  | "dropped"
  | "unknown";

export type DetailField = {
  label: string;
  value: string | number | boolean | null | undefined;
};

export type IntegrationMessageRow = {
  id: string;
  label: string;
  model: string;
  statusText: string;
  statusVariant: StatusVariant;
  created_at?: string;
  detailFields: DetailField[];
  orderMessageId?: number;
  integrationOrderSourceTable: string;
};

const KNOWN_STATUS_VARIANTS: StatusVariant[] = [
  "success",
  "error",
  "pending",
  "hold",
  "dropped",
];

export const toStatusVariant = (status?: string | null): StatusVariant => {
  const normalized = status?.toLowerCase() || "";
  if ((KNOWN_STATUS_VARIANTS as string[]).includes(normalized)) {
    return normalized as StatusVariant;
  }
  if (normalized.includes("success") || normalized.includes("complete")) {
    return "success";
  }
  if (normalized.includes("error") || normalized.includes("fail")) {
    return "error";
  }
  if (
    normalized.includes("pending") ||
    normalized.includes("processing") ||
    normalized.includes("waiting") ||
    normalized.includes("queue")
  ) {
    return "pending";
  }
  return "unknown";
};

const field = (label: string, value: DetailField["value"]): DetailField => ({
  label,
  value,
});

// e.g. "wpl_order_messages" -> "wplordermessage"
const toIntegrationOrderSourceTable = (key: string): string =>
  key.replace(/_/g, "").replace(/s$/, "");

type MessageTypeConfig<T> = {
  key: keyof CaseIntegrationMessage;
  label: string;
  model: string;
  toRow: (
    message: T
  ) => Pick<
    IntegrationMessageRow,
    "statusText" | "statusVariant" | "detailFields"
  >;
};

const MESSAGE_TYPES: MessageTypeConfig<any>[] = [
  {
    key: "autofax_messages",
    label: "Autofax",
    model: "AutofaxMessage",
    toRow: (m: AutofaxMessage) => ({
      statusText: m.status,
      statusVariant: toStatusVariant(m.status),
      detailFields: [
        field("Clinic", m.clinic_name),
        field("Fax Number", m.fax_number),
      ],
    }),
  },
  {
    key: "wpl_order_messages",
    label: "WPL Order",
    model: "WPLOrderMessage",
    toRow: (m: WPLOrderMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [
        field("Message Trigger", m.message_trigger),
        field("Error Reason", m.error_reason),
      ],
    }),
  },
  {
    key: "sonic_order_messages",
    label: "Sonic Order",
    model: "SonicOrderMessage",
    toRow: (m: SonicOrderMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [
        field("Error Reason", m.error_reason),
        field("Duplicate Case", m.duplicate_case),
        field("EMR Identity", m.emr_identity),
      ],
    }),
  },
  {
    key: "modmed_order_messages",
    label: "ModMed Order",
    model: "ModMedOrderMessage",
    toRow: (m: ModMedOrderMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [field("Duplicate Case", m.duplicate_case)],
    }),
  },
  {
    key: "ecw_order_messages",
    label: "ECW Order",
    model: "ECWOrderMessage",
    toRow: (m: ECWOrderMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [field("Duplicate Case", m.duplicate_case)],
    }),
  },
  {
    key: "wpl_result_messages",
    label: "WPL Result",
    model: "WPLResultMessage",
    toRow: (m: WPLResultMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [field("Error Reason", m.error_reason)],
    }),
  },
  {
    key: "modmed_result_messages",
    label: "ModMed Result",
    model: "ModMedResultMessage",
    toRow: (m: ModMedResultMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [field("Error Reason", m.error_reason)],
    }),
  },
  {
    key: "sonic_result_messages",
    label: "Sonic Result",
    model: "SonicResultMessage",
    toRow: (m: SonicResultMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [field("Error Reason", m.error_reason)],
    }),
  },
  {
    key: "ezderm_result_messages",
    label: "EZDerm Result",
    model: "EZDermResultMessage",
    toRow: (m: EZDermResultMessage) => ({
      statusText: m.ezderm_status || "",
      statusVariant: toStatusVariant(m.ezderm_status),
      detailFields: [field("EZDerm Response", m.ezderm_response)],
    }),
  },
  {
    key: "nextech_result_messages",
    label: "Nextech Result",
    model: "NextechResultMessage",
    toRow: (m: NextechResultMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [field("Error Reason", m.error_reason)],
    }),
  },
  {
    key: "ecw_result_messages",
    label: "ECW Result",
    model: "ECWResultMessage",
    toRow: (m: ECWResultMessage) => ({
      statusText: m.status || "",
      statusVariant: toStatusVariant(m.status),
      detailFields: [field("Error Reason", m.error_reason)],
    }),
  },
];

export const flattenIntegrationMessages = (
  data: CaseIntegrationMessage | null | undefined
): IntegrationMessageRow[] => {
  if (!data) return [];

  const rows = MESSAGE_TYPES.flatMap((typeConfig) => {
    const messages = (data[typeConfig.key] || []) as any[];
    return messages.map((message, index) => {
      const row = {
        id: `${typeConfig.key}-${index}`,
        label: typeConfig.label,
        model: typeConfig.model,
        created_at: message.created_at,
        orderMessageId: message.id,
        integrationOrderSourceTable: toIntegrationOrderSourceTable(
          typeConfig.key
        ),
        ...typeConfig.toRow(message),
      };
      return {
        ...row,
        detailFields: row.detailFields.filter(
          (f) =>
            !(
              f.label === "Error Reason" &&
              (f.value === null || f.value === undefined)
            )
        ),
      };
    });
  });

  return rows.sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bTime - aTime;
  });
};
